import { useCallback, useMemo, useSyncExternalStore } from "react";
import jsonLogic from "json-logic-js";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { itemModificationRuleType, NoteAttributesType } from "lib/types/checkout";
import { isEmptyObj } from "lib/utils/helpers";
import { getRuleLocalStorageSnapshot, subscribeRuleLocalStorage } from "lib/utils/ruleLocalStorage";

/**
 * A rule returns the reason the CTA is blocked, so an empty string means allowed.
 * An unset rule makes jsonLogic return the rule itself, hence the object check.
 */
const asBlockedReason = (result: unknown): string => {
  if (typeof result === "string") return result;
  if (typeof result === "boolean") return result ? "" : " ";
  if (result == null) return "";
  if (typeof result === "object" && Object.keys(result as object).length === 0) return "";
  return String(result);
};

/**
 * Note attributes are an array of {name, value}, which JSONLogic cannot look up by key.
 * Rules address them as `noteAttributesMap.<name>` instead.
 */
const toNoteAttributesMap = (attributes: NoteAttributesType[]): Record<string, string> =>
  (attributes ?? []).reduce<Record<string, string>>((map, attribute) => {
    if (attribute?.name) map[attribute.name] = attribute.value;
    return map;
  }, {});

export const useJsonPermission = () => {
  const {
    state: { rawCheckoutObject },
  } = useCheckoutContext();
  const {
    state: { cartMetadata, checkoutMetadata, ssoMetadata },
  } = useMerchantContext();

  // Persists across reloads; rules read it as `localStorage.<key>`.
  const localStorageData = useSyncExternalStore(subscribeRuleLocalStorage, getRuleLocalStorageSnapshot);

  const ruleData = useMemo(
    () => ({ ...(rawCheckoutObject ?? {}), localStorage: localStorageData }),
    [rawCheckoutObject, localStorageData],
  );

  const cartPermissions = useMemo(() => {
    const checkoutBtnStatus = asBlockedReason(
      jsonLogic.apply(cartMetadata?.uiAttributes?.primaryCtaDisabilityRule ?? "", ruleData),
    );
    const loginBtnStatus = asBlockedReason(
      jsonLogic.apply(cartMetadata?.uiAttributes?.jsonLogicRules?.loginButtonCtaRule ?? "", ruleData),
    );

    return { checkoutBtnStatus, loginBtnStatus };
  }, [ruleData, cartMetadata]);

  const checkoutPermissions = useMemo(() => {
    const loginBtnStatus = asBlockedReason(
      jsonLogic.apply(checkoutMetadata?.uiAttributes?.jsonLogicRules?.loginButtonCtaRule ?? "", ruleData),
    );

    return { loginBtnStatus };
  }, [ruleData, checkoutMetadata]);

  // SSO metadata is raw backend passthrough, so the key stays snake_case here.
  const ssoPermissions = useMemo(() => {
    const loginBtnStatus = asBlockedReason(
      jsonLogic.apply(ssoMetadata?.json_logic_rules?.login_button_cta_rule ?? "", ruleData),
    );

    return { loginBtnStatus };
  }, [ruleData, ssoMetadata]);

  /**
   * Evaluated per item rather than against the checkout, so this is a function.
   * An unset or empty rule leaves every modification allowed.
   */
  const getItemModificationRules = useCallback(
    (rawItem: unknown): itemModificationRuleType => {
      const resolved = jsonLogic.apply(cartMetadata?.uiAttributes?.itemModificationRule ?? "", rawItem);

      if (!Boolean(resolved) || isEmptyObj(resolved)) {
        return {
          showDelete: true,
          showIncrement: true,
          showDecrement: true,
          redirectOnClick: true,
          showQuantityEditor: true,
        };
      }

      return {
        showDelete: Boolean(resolved?.delete),
        showIncrement: Boolean(resolved?.increase),
        showDecrement: Boolean(resolved?.decrease),
        redirectOnClick: resolved?.redirectOnClick === false ? false : true,
        showQuantityEditor: Boolean(resolved?.showQuantityEditor),
      };
    },
    [cartMetadata?.uiAttributes?.itemModificationRule],
  );

  return {
    cartPermissions,
    checkoutPermissions,
    ssoPermissions,
    getItemModificationRules,
  };
};
